/**
 * ClaudeIdentitiesPanel — app-level Settings UI for named Claude identities.
 *
 * Each identity is an isolated `CLAUDE_CONFIG_DIR` (its own login + sessions), so
 * a work (SSO) account and a personal account can coexist permanently, scoped
 * per project. This panel lists identities, lets you add/edit/delete them, pick
 * the global default, and shows live auth status read from each dir's
 * `.credentials.json` (no network). The Authenticate / Logout actions run the
 * real `claude auth` flow in a terminal scoped to the identity — wired in
 * ticket 075 via the `onAuthenticate` / `onLogout` handlers below.
 *
 * Backing surface: `backend.identity.*` (CefIdentityBackend → core `identity.*`).
 */
import { useCallback, useEffect, useState } from 'react';
import { Key, Plus, Trash2, Star, LogIn, LogOut, CheckCircle2, XCircle, ShieldCheck } from 'lucide-react';
import { useBackend } from '@/services/backend';
import type { ClaudeIdentity, AuthStatus } from '@/services/backend/IBackend';
import { logger } from '@/lib/logger';
import { useProjectStore } from '@/stores/projectStore';
import { useTerminalStore } from '@/stores/terminalStore';
import { useUIStore } from '@/stores/uiStore';

const TAG = 'ClaudeIdentitiesPanel';

interface IdentityForm {
  /** the identity being edited, or '' when adding a new one */
  original: string;
  name: string;
  configDir: string;
  email: string;
  sso: boolean;
}

const EMPTY_FORM: IdentityForm = { original: '', name: '', configDir: '', email: '', sso: false };

function relativeExpiry(expiresAt?: number | null): string | null {
  if (!expiresAt) return null;
  const ms = expiresAt - Date.now();
  if (ms <= 0) return 'expired';
  const days = Math.floor(ms / 86_400_000);
  if (days >= 1) return `expires in ${days}d`;
  const hours = Math.floor(ms / 3_600_000);
  if (hours >= 1) return `expires in ${hours}h`;
  return 'expires soon';
}

export default function ClaudeIdentitiesPanel() {
  const backend = useBackend();
  const currentProject = useProjectStore((s) => s.currentProject);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [identities, setIdentities] = useState<ClaudeIdentity[]>([]);
  const [statuses, setStatuses] = useState<Record<string, AuthStatus>>({});
  const [form, setForm] = useState<IdentityForm | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const list = await backend.identity.list();
      setIdentities(list);
      // Fetch auth status per identity (file read; cheap, parallel).
      const entries = await Promise.all(
        list.map(async (i) => {
          try {
            return [i.name, await backend.identity.status(i.configDir)] as const;
          } catch {
            return [i.name, { authenticated: false, expiresAt: null, subscriptionType: null }] as const;
          }
        }),
      );
      setStatuses(Object.fromEntries(entries));
    } catch (e) {
      logger.warn(TAG, 'load failed', e);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [backend]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const saveForm = useCallback(async () => {
    if (!form) return;
    const name = form.name.trim();
    const configDir = form.configDir.trim();
    if (!name || !configDir) {
      setError('Name and config dir are required.');
      return;
    }
    setBusy('save');
    try {
      // Renaming an identity: remove the old row first (name is the PK).
      if (form.original && form.original !== name) {
        await backend.identity.remove(form.original);
      }
      await backend.identity.upsert({ name, configDir, email: form.email.trim() || undefined, sso: form.sso });
      setForm(null);
      await load();
    } catch (e) {
      logger.warn(TAG, 'save failed', e);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }, [backend, form, load]);

  const remove = useCallback(async (name: string) => {
    setBusy(name);
    try {
      await backend.identity.remove(name);
      await load();
    } catch (e) {
      logger.warn(TAG, 'delete failed', e);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }, [backend, load]);

  const setDefault = useCallback(async (name: string) => {
    setBusy(name);
    try {
      await backend.identity.setDefault(name);
      await load();
    } catch (e) {
      logger.warn(TAG, 'setDefault failed', e);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }, [backend, load]);

  /**
   * Open a terminal scoped to `identity.configDir` (via the env override that
   * core's resolution treats as highest-precedence) and run a `claude auth …`
   * command in it. Uses the same pending-command → TerminalTab path RunButton
   * uses, then switches to the terminal tab and closes Settings so the user can
   * complete the interactive OAuth/SSO flow.
   */
  const runAuthCommand = useCallback((identity: ClaudeIdentity, command: string, titlePrefix: string) => {
    if (!currentProject) {
      setError('Open a project first — the auth terminal opens in the active project.');
      return;
    }
    useTerminalStore.getState().setPendingCommand({
      command,
      title: `${titlePrefix} — ${identity.name}`,
      projectPath: currentProject.root_path,
      env: { CLAUDE_CONFIG_DIR: identity.configDir },
      kind: 'auth',
    });
    useTerminalStore.getState().requestTabSwitch();
    useUIStore.getState().closeSettings();
  }, [currentProject]);

  const onAuthenticate = useCallback((identity: ClaudeIdentity) => {
    // `claude auth login` opens the browser for OAuth/SSO; --sso forces the SSO
    // flow (employer-gated accounts) and --email prefills the login page.
    let cmd = 'claude auth login';
    if (identity.sso) cmd += ' --sso';
    if (identity.email) cmd += ` --email ${identity.email}`;
    runAuthCommand(identity, cmd, 'Sign in');
  }, [runAuthCommand]);

  const onLogout = useCallback((identity: ClaudeIdentity) => {
    runAuthCommand(identity, 'claude auth logout', 'Sign out');
  }, [runAuthCommand]);

  if (loading) {
    return <div className="flex items-center justify-center py-12"><span className="loading loading-spinner loading-md" /></div>;
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Key className="w-4 h-4 text-primary" />
          <h3 className="font-medium">Claude Identities</h3>
        </div>
        <p className="text-sm opacity-70">
          Each identity is an isolated <code className="text-xs">CLAUDE_CONFIG_DIR</code> — its own login and
          sessions. Pick a default for all projects; a project overrides it via
          <code className="text-xs"> claudeIdentity</code> in its config. Terminals (and the agents they launch)
          run under the resolved identity automatically.
        </p>
      </div>

      {error && (
        <div className="alert alert-warning text-sm py-2">
          <XCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}

      {identities.length === 0 && (
        <div className="text-sm opacity-60 border border-dashed border-base-300 rounded-lg p-4 text-center">
          No identities yet. Add one to isolate a work (SSO) login from your personal account.
        </div>
      )}

      <div className="space-y-2">
        {identities.map((id) => {
          const st = statuses[id.name];
          const exp = relativeExpiry(st?.expiresAt);
          return (
            <div key={id.name} className="border border-base-300 rounded-lg p-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium truncate">{id.name}</span>
                  {id.isDefault && (
                    <span className="badge badge-sm badge-primary gap-1"><Star className="w-3 h-3" /> default</span>
                  )}
                  {id.sso && (
                    <span className="badge badge-sm badge-ghost gap-1"><ShieldCheck className="w-3 h-3" /> SSO</span>
                  )}
                  {st?.authenticated ? (
                    <span className="badge badge-sm badge-success gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Signed in{st.subscriptionType ? ` · ${st.subscriptionType}` : ''}
                    </span>
                  ) : (
                    <span className="badge badge-sm badge-ghost gap-1">
                      <XCircle className="w-3 h-3" /> {exp === 'expired' ? 'Expired' : 'Not signed in'}
                    </span>
                  )}
                  {st?.authenticated && exp && exp !== 'expired' && (
                    <span className="text-xs opacity-50">{exp}</span>
                  )}
                </div>
                <div className="text-xs opacity-60 truncate mt-0.5 font-mono">{id.configDir}</div>
                {id.email && <div className="text-xs opacity-60 truncate">{id.email}</div>}
              </div>

              <div className="flex items-center gap-1 shrink-0">
                {st?.authenticated ? (
                  <button className="btn btn-ghost btn-xs gap-1" disabled={busy === id.name} onClick={() => onLogout(id)} title="Sign out of this identity">
                    <LogOut className="w-3.5 h-3.5" /> Logout
                  </button>
                ) : (
                  <button className="btn btn-ghost btn-xs gap-1" disabled={busy === id.name} onClick={() => onAuthenticate(id)} title="Sign in to this identity">
                    <LogIn className="w-3.5 h-3.5" /> {exp === 'expired' ? 'Re-authenticate' : 'Authenticate'}
                  </button>
                )}
                {!id.isDefault && (
                  <button className="btn btn-ghost btn-xs gap-1" disabled={busy === id.name} onClick={() => setDefault(id.name)} title="Make this the default identity">
                    <Star className="w-3.5 h-3.5" /> Default
                  </button>
                )}
                <button
                  className="btn btn-ghost btn-xs"
                  disabled={busy === id.name}
                  onClick={() => setForm({ original: id.name, name: id.name, configDir: id.configDir, email: id.email ?? '', sso: id.sso })}
                  title="Edit"
                >
                  Edit
                </button>
                <button className="btn btn-ghost btn-xs text-error" disabled={busy === id.name} onClick={() => remove(id.name)} title="Delete">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {form ? (
        <div className="border border-base-300 rounded-lg p-3 space-y-3">
          <div className="font-medium text-sm">{form.original ? `Edit "${form.original}"` : 'Add identity'}</div>
          <div className="grid grid-cols-1 gap-2">
            <label className="form-control">
              <span className="label-text text-xs mb-1">Name</span>
              <input
                className="input input-bordered input-sm"
                placeholder="work-alchemy"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label className="form-control">
              <span className="label-text text-xs mb-1">Config dir</span>
              <input
                className="input input-bordered input-sm font-mono"
                placeholder={form.name ? `~/.claude-${form.name}` : '~/.claude-work'}
                value={form.configDir}
                onChange={(e) => setForm({ ...form, configDir: e.target.value })}
              />
            </label>
            <label className="form-control">
              <span className="label-text text-xs mb-1">Email (optional, prefilled on the login page)</span>
              <input
                className="input input-bordered input-sm"
                placeholder="you@company.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </label>
            <label className="label cursor-pointer justify-start gap-2">
              <input type="checkbox" className="checkbox checkbox-sm" checked={form.sso} onChange={(e) => setForm({ ...form, sso: e.target.checked })} />
              <span className="label-text text-sm">Use SSO login flow (<code className="text-xs">claude auth login --sso</code>)</span>
            </label>
          </div>
          <div className="flex items-center gap-2">
            <button className="btn btn-primary btn-sm" disabled={busy === 'save'} onClick={saveForm}>
              {busy === 'save' ? <span className="loading loading-spinner loading-xs" /> : 'Save'}
            </button>
            <button className="btn btn-ghost btn-sm" disabled={busy === 'save'} onClick={() => setForm(null)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="btn btn-outline btn-sm gap-1" onClick={() => setForm({ ...EMPTY_FORM })}>
          <Plus className="w-4 h-4" /> Add identity
        </button>
      )}
    </div>
  );
}
